/* global ALGOLIASEARCH_APP_ID, ALGOLIASEARCH_SEARCH_KEY, QUERY_SUGGESTION_INDEX */
import algoliasearch from 'algoliasearch'
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import {
  trackSearchPreviewClick,
  trackAutocompleteClick,
  trackAutocompleteOpen,
  trackNavbarSearch
} from '../../analytics/events/browsing'
import { getCookie } from '../../utils/cookieHelpers'
import { getLocaleFromPath, prependLocale } from '../../utils/localeHelpers'
import { t } from './autocompleteTranslations'
import { typesenseEnabled } from '../../libs/search/searchClient'
import { searchAutocompleteCollections } from '../../libs/search/typesenseAutocomplete'

const locale = getLocaleFromPath(window.location.pathname)
const localeUnderscore = locale ? locale.replace(/-/g, '_') : null
const nodeEnv = process.env.NODE_ENV
const listingIndex = localeUnderscore ? `Listing_${localeUnderscore}_${nodeEnv}` : `Listing_${nodeEnv}`
const querySuggestionIndex = localeUnderscore ? `Listing_${localeUnderscore}_${nodeEnv}_query_suggestions` : QUERY_SUGGESTION_INDEX

// autocomplete-js has no built-in query debounce, so wrap a source's async
// getItems in the debounced-promise helper from Algolia's docs: a burst of
// keystrokes collapses to a single query.
function debouncePromise (fn, time = 200) {
  const timer = {}
  return (...args) => new Promise(resolve => {
    clearTimeout(timer.id)
    timer.id = setTimeout(() => resolve(fn(...args)), time)
  })
}

function debounceTypedQueries (getItems) {
  const debounced = debouncePromise(getItems)
  // Only query while the visitor is typing; no request on focus (empty query).
  return params => (params.query ? debounced(params) : [])
}

// Both dropdown halves come from one multi_search; sources call getItems
// independently, so the in-flight promise is memoised by query (latest only,
// or every keystroke's response would leak for the page's life). A failure is
// swallowed, never taking the header down, and evicted so retyping retries.
function createTypesenseFetcher () {
  let lastQuery = null
  let lastPromise = null

  return function fetchBoth (query) {
    if (query !== lastQuery) {
      lastQuery = query
      lastPromise = searchAutocompleteCollections(query).catch(error => {
        if (lastQuery === query) lastQuery = null
        if (window.Sentry) window.Sentry.captureException(error)
        return { listings: [], suggestions: [] }
      })
    }
    return lastPromise
  }
}

export default function searchAutocomplete () {
  const onTypesense = typesenseEnabled()
  const algoliaClient = onTypesense ? null : algoliasearch(ALGOLIASEARCH_APP_ID, ALGOLIASEARCH_SEARCH_KEY)
  const fetchTypesense = onTypesense ? createTypesenseFetcher() : null
  const placeholder = document.querySelector('#vjs-aa-placeholder')
  const placeholderMobile = document.querySelector('#vjs-aa-placeholder-mobile')
  if (!placeholder || !placeholderMobile) return

  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: locale ? `RECENT_SEARCH_${locale}` : 'RECENT_SEARCH',
    limit: 4,
    transformSource ({ source }) {
      return {
        ...source,
        onSelect ({ item }) {
          trackAutocompleteClick('recentQuery', null)
          trackSearchPreviewClick()
          trackNavbarSearch(item.label, false, true)
        },
        getItemUrl ({ item }) {
          return createUrl(item.label, item.category)
        },
        templates: {
          item (params) {
            const { item, html } = params
            return html`<a class="aa-ItemLink" href="${createUrl(item.label, item.category)}">
              ${source.templates.item(params).props.children}
            </a>`
          }
        }
      }
    }
  })

  // Algolia's query-suggestions plugin. Built only on the Algolia path — the
  // Typesense equivalent is typesenseSuggestionsSource below.
  const buildQuerySuggestionsPlugin = () => createQuerySuggestionsPlugin({
    searchClient: algoliaClient,
    indexName: querySuggestionIndex,
    categoryAttribute: [
      listingIndex,
      'facets',
      'exact_matches',
      'category_name'
    ],
    itemsWithCategories: 2,
    categoriesPerItem: 2,
    getSearchParams () {
      return { hitsPerPage: 5 }
    },
    transformSource ({ source }) {
      return {
        ...source,
        getItems: debounceTypedQueries(params => source.getItems(params)),
        onSelect ({ item }) {
          trackSearchPreviewClick()
          trackAutocompleteClick('querySuggestion', null)
          trackNavbarSearch(item.query, true, false)
        },
        getItemUrl ({ item }) {
          return createUrl(item.query, item.__autocomplete_qsCategory)
        },
        templates: {
          item (params) {
            const { item, html } = params
            return html`<a class="aa-ItemLink" href="${createUrl(item.query, item.__autocomplete_qsCategory)}">
              ${source.templates.item(params).props.children}
            </a>`
          }
        }
      }
    }
  })

  // Typesense replacement for the query-suggestions plugin: categories are
  // resolved at seed time and expanded by withCategoryRows, so a plain
  // source is all that is left.
  const typesenseSuggestionsSource = {
    sourceId: 'querySuggestions',
    getItems: debounceTypedQueries(({ query }) => fetchTypesense(query).then(r => r.suggestions)),
    // Arrowing onto a suggestion puts it in the search box (the plugin's
    // behaviour; autocomplete-core's default keeps what was typed).
    getItemInputValue ({ item }) {
      return item.query
    },
    onSelect ({ item }) {
      trackSearchPreviewClick()
      trackAutocompleteClick('querySuggestion', null)
      trackNavbarSearch(item.query, true, false)
    },
    getItemUrl ({ item }) {
      return createUrl(item.query, item.__autocomplete_qsCategory)
    },
    templates: {
      item ({ item, components, html }) {
        const category = item.__autocomplete_qsCategory
        const url = createUrl(item.query, category)

        // Reuses the classic theme's markup verbatim — copying the class
        // names keeps this pixel-identical to the Algolia dropdown with no
        // new CSS.
        if (category) {
          return html`<a class="aa-ItemLink" href="${url}">
            <div class="aa-ItemContent aa-ItemContent--indented">
              <div class="aa-ItemContentSubtitle aa-ItemContentSubtitle--standalone">
                <span class="aa-ItemContentSubtitleIcon"></span>
                <span>
                  ${t('in_category')} ${' '}
                  <span class="aa-ItemContentSubtitleCategory">${category}</span>
                </span>
              </div>
            </div>
          </a>`
        }

        return html`<a class="aa-ItemLink" href="${url}">
          <div class="aa-ItemContent">
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentTitle">
                ${components.Highlight({ hit: item, attribute: 'query' })}
              </div>
            </div>
          </div>
        </a>`
      }
    }
  }

  placeholder.remove()
  placeholderMobile.remove()

  const searchInputs = ['#vjs-autocomplete-container', '#vjs-autocomplete-container-mobile']
  const autocompleteInstances = []

  searchInputs.forEach(searchInput => {
    const fetchAlgoliaListings = ({ query, setContext }) => getAlgoliaResults({
      searchClient: algoliaClient,
      queries: [
        {
          indexName: listingIndex,
          query,
          params: {
            userToken: getCookie('_ALGOLIA'),
            hitsPerPage: 5,
            clickAnalytics: true,
            analytics: true,
            getRankingInfo: true
          }
        }
      ],
      transformResponse ({ results, hits }) {
        if (results[0]?.index === listingIndex) {
          setAlgoliaTestAllocation(setContext, results)
        }

        return hits
      }
    })

    // Shares the multi_search with the suggestions source, so both halves of the
    // dropdown cost one request.
    const fetchTypesenseListings = ({ query }) => fetchTypesense(query).then(r => r.listings)

    const debouncedFetchListings = debouncePromise(
      onTypesense ? fetchTypesenseListings : fetchAlgoliaListings
    )

    const autocompleteInstance = autocomplete({
      container: searchInput,
      detachedMediaQuery: 'none',
      placeholder: t('search_placeholder'),
      insights: !onTypesense,
      onStateChange ({ state }) {
        if (!window.hasFiredAutocompleteOpen) {
          if (state.isOpen) {
            window.hasFiredAutocompleteOpen = true
            trackAutocompleteOpen()
          }
        }
      },
      onSubmit ({ state, event }) {
        const query = state.query
        trackNavbarSearch(query, false, false)
        window.location.href = prependLocale(`/search?q=${encodeURIComponent(query)}`, locale)
      },
      // On Typesense the suggestions half is a source rather than a plugin, so the
      // plugin list is just recent searches (localStorage only, engine-agnostic).
      plugins: onTypesense
        ? [recentSearchesPlugin]
        : [recentSearchesPlugin, buildQuerySuggestionsPlugin()],
      getSources ({ query }) {
        // Only query listings once the visitor starts typing. On focus (empty
        // query) the panel still opens for recent searches and query
        // suggestions, but we skip the "recommended for you" request.
        if (!query) return []

        // Suggestions first, matching where the Algolia plugin's source sat.
        const sources = onTypesense ? [typesenseSuggestionsSource] : []

        return [
          ...sources,
          {
            sourceId: 'listings',
            getItems: debouncedFetchListings,

            onSelect ({ item }) {
              trackAutocompleteClick('listing', item.objectID)
              trackSearchPreviewClick()
            },

            getItemUrl ({ item }) {
              return prependLocale(`/${item.path_hierarchy}/${item.slug}`, locale)
            },

            templates: {
              header ({ state, html }) {
                return headerTemplate(state, html)
              },

              noResults () {
                return t('no_results')
              },

              item ({ item, components, html }) {
                return itemTemplate(item, components, html)
              }
            }
          }
        ]
      },
      openOnFocus: true,
      navigator: {
        navigate ({ itemUrl }) {
          window.location.assign(itemUrl)
        },
        navigateNewTab ({ itemUrl }) {
          const windowReference = window.open(itemUrl, '_blank', 'noopener')

          if (windowReference) {
            windowReference.focus()
          }
        },
        navigateNewWindow ({ itemUrl }) {
          window.open(itemUrl, '_blank', 'noopener')
        }
      }
    })
    autocompleteInstances.push(autocompleteInstance)
  })

  const aaInputElements = Array.from(document.getElementsByClassName('aa-Input'))
  aaInputElements.forEach(inputEl => inputEl.addEventListener('blur', () => {
    autocompleteInstances.forEach(instance => instance.setIsOpen(false))
  }))
}

function setAlgoliaTestAllocation (setContext, results) {
  const abTestVariantID = results[0].abTestVariantID
  if (abTestVariantID) {
    setContext({
      abTestVariant: abTestVariantID
    })

    const expVersion = abTestVariantID === 1 ? 'control' : 'variant'
    window.dataLayer.push({
      event: 'ce - amplitude - setAlgoliaAllocations',
      user: {
        enabled_flipper_features: {
          algolia_generic_ab: expVersion
        }
      }
    })
  }
}

function headerTemplate (state, html) {
  const headerLabel = (state.context.abTestVariant === undefined || state.context.abTestVariant === 2) ? t('recommended_for_you') : t('recommended')

  return html`
    <div class="flex gap-1">
      <span class="aa-SourceHeaderTitle flex items-center gap-2 text-atlantic-green">${headerLabel}</span>
      <div class="aa-SourceHeaderLine opacity-20"></div>
    </div>
  `
}

function itemTemplate (item, components, html) {
  return html`
    <div class="aa-ItemWrapper">
      <a class="aa-ItemContent no-underline" href="${prependLocale(`/${item.path_hierarchy}/${item.slug}`, locale)}">
        <div class="aa-ItemIcon aa-ItemIcon--alignTop">
          <img src="${item.main_photo?.image?.thumb_webp?.url}" alt="${item.title}" />
        </div>
        <div class="aa-ItemContentBody">
          <div class="aa-ItemContentTitle">
            ${components.Highlight({ hit: item, attribute: 'title' })}
          </div>
        </div>
        <div class="aa-ItemActions">
          <button class="aa-ItemActionButton aa-DesktopOnly aa-ActiveOnly" type="button" title="Select">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M18.984 6.984h2.016v6h-15.188l3.609 3.609-1.406 1.406-6-6 6-6 1.406 1.406-3.609 3.609h13.172v-4.031z" />
            </svg>
          </button>
        </div>
      </a>
    </div>
  `
}

function createUrl (query, category) {
  const urlParams = new URLSearchParams()
  urlParams.set('q', query)
  if (category) {
    urlParams.set('hFR[category_hierarchy.lvl0][0]', category)
  }

  return prependLocale(`/search?${urlParams.toString()}`, locale)
}
